import { List, ListItem, Row, Skeleton, Tab, Tabs } from '@fs/zion-ui'
import React, { useMemo } from 'react'
import { useHistory } from '@fs/zion-router'
import { ContentHistory, MenuSettings, NoticeProtected, PersonDuplicates } from '@fs/zion-icon'
import { getTabUrl, useTreeSettingsRouteMatch, type TabName } from '../util'
import { useMembersContext } from '../../providers/MembersProvider'

type Props = {
  tab: TabName
}

export default function SideNav({ tab }: Props): JSX.Element {
  const match = useTreeSettingsRouteMatch()
  const history = useHistory()
  const { userIsAdmin } = useMembersContext()

  return (
    <nav>
      <List>
        <ListItem
          data-testid="details-nav-item"
          startElement={<ListItem.Icon Icon={MenuSettings} />}
          selected={tab === 'details'}
          primaryText="Tree Details"
          // Use "onClick" over "to" so selected state can be set
          onClick={() => history.push(getTabUrl('details', match))}
        />
        <ListItem
          data-testid="latest-changes-nav-item"
          startElement={<ListItem.Icon Icon={ContentHistory} />}
          selected={tab === 'latest-changes'}
          primaryText="Latest Changes"
          // Use "onClick" over "to" so selected state can be set
          onClick={() => history.push(getTabUrl('latest-changes', match))}
        />
        {userIsAdmin && (
          <ListItem
            data-testid="privacy-nav-item"
            startElement={<ListItem.Icon Icon={NoticeProtected} />}
            selected={tab === 'privacy'}
            primaryText="Privacy Settings"
            // Use "onClick" over "to" so selected state can be set
            onClick={() => history.push(getTabUrl('privacy', match))}
          />
        )}
        <ListItem
          data-testid="contributors-nav-item"
          startElement={<ListItem.Icon Icon={PersonDuplicates} />}
          selected={tab === 'contributors'}
          primaryText="Manage Contributors"
          // Use "onClick" over "to" so selected state can be set
          onClick={() => history.push(getTabUrl('contributors', match))}
        />
      </List>
    </nav>
  )
}

export function TopNav({ tab }: Props): JSX.Element {
  const match = useTreeSettingsRouteMatch()
  const { userIsAdmin } = useMembersContext()

  const tabItems: { key: TabName; title: string }[] = useMemo(
    () => [
      { key: 'details', title: 'Tree Details' },
      { key: 'latest-changes', title: 'Latest Changes' },
      ...(userIsAdmin ? ([{ key: 'privacy', title: 'Privacy Settings' }] as const) : []),
      { key: 'contributors', title: 'Manage Contributors' },
    ],
    [userIsAdmin]
  )

  const currentIndex = tabItems.findIndex(({ key }) => key === tab)

  return (
    <Tabs fullWidth index={currentIndex}>
      {tabItems.map(({ key, title }) => (
        <Tab key={key} linkName={`${title} Tab`} title={title} to={getTabUrl(key, match)} />
      ))}
    </Tabs>
  )
}

export function SideNavSkeleton(): JSX.Element {
  return (
    <nav>
      <List>
        <Skeleton.ListItem hasIcon />
        <Skeleton.ListItem hasIcon />
        <Skeleton.ListItem hasIcon />
      </List>
    </nav>
  )
}

export function TopNavSkeleton(): JSX.Element {
  return (
    <Row>
      {/* eslint-disable-next-line test-selectors/button -- NA */}
      <Skeleton.Button growX maxWidth="100%" />
      {/* eslint-disable-next-line test-selectors/button -- NA */}
      <Skeleton.Button growX maxWidth="100%" />
      {/* eslint-disable-next-line test-selectors/button -- NA */}
      <Skeleton.Button growX maxWidth="100%" />
    </Row>
  )
}
